import { Checkbox } from "@/components/ui/Checkbox";
import { cn } from "@/utils/cn";
import { useId, useState, type ReactNode } from "react";
import { LABEL } from "./styles";

/** Collapsible group of advanced flags, collapsed by default so the common
 * path stays uncluttered. A count badge shows how many flags are active. */
export const AdvancedSection = ({
  title,
  activeCount,
  children,
}: {
  title: string;
  /** Number of non-default flags inside — shown so collapsed ≠ hidden. */
  activeCount: number;
  children: ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-zinc-200/70 pt-3">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="font-ui flex w-full items-baseline gap-2 text-sm leading-6 text-zinc-500 transition-colors hover:text-zinc-900 focus-visible:outline-none focus-visible:text-zinc-900"
      >
        <span aria-hidden className="w-3 text-zinc-400">
          {open ? "−" : "+"}
        </span>
        {title}
        {activeCount > 0 && (
          <span className="font-mono text-2xs text-zinc-400">
            {activeCount} set
          </span>
        )}
      </button>
      {open && <div className="mt-4 space-y-5 pl-5">{children}</div>}
    </div>
  );
};

/** One boolean CLI flag as a labelled checkbox row. */
export const FlagCheckbox = ({
  label,
  flag,
  checked,
  onChange,
  disabled,
  disabledReason,
}: {
  label: string;
  /** The literal flag, shown in mono next to the label (e.g. `-i`). */
  flag: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  /** Tooltip explaining why the flag is unavailable. */
  disabledReason?: string;
}) => {
  const id = useId();
  return (
    <label
      htmlFor={id}
      title={disabled ? disabledReason : undefined}
      className={cn(
        "flex items-center gap-2.5 text-sm leading-6 text-zinc-700",
        disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer",
      )}
    >
      <Checkbox
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(v) => onChange(v === true)}
      />
      <span>{label}</span>
      <code className="ml-auto font-mono text-2xs text-zinc-400">{flag}</code>
    </label>
  );
};

/** Compact labelled field wrapper for advanced inputs/selects. */
export const AdvancedField = ({
  label,
  flag,
  htmlFor,
  children,
}: {
  label: string;
  flag: string;
  htmlFor?: string;
  children: ReactNode;
}) => (
  <div>
    <label htmlFor={htmlFor} className={`${LABEL} flex items-baseline gap-2`}>
      {label}
      <code className="font-mono text-3xs normal-case tracking-normal text-zinc-300">{flag}</code>
    </label>
    {children}
  </div>
);
